import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { catchError, from, switchMap, throwError } from 'rxjs';
import { ProblemDetails, isProblemDetails } from './api-error.model';

/**
 * Normalizes all HTTP error responses to {@link ProblemDetails}.
 *
 * - Synthesizes a ProblemDetails for non-JSON errors (network failures, etc.).
 * - Logs machine-readable error codes to the console in development.
 * - Re-throws a normalized ProblemDetails so downstream code never sees raw HttpErrorResponse.
 *
 * Must be registered FIRST in `withInterceptors` so it runs last on responses,
 * wrapping any errors that pass through the auth and tenant interceptors.
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  return next(req).pipe(
    catchError((httpError: HttpErrorResponse) =>
      // A file download's error body arrives as a Blob; read it first so its ProblemDetails come through.
      httpError.error instanceof Blob
        ? from(withJsonBody(httpError)).pipe(switchMap(rethrowAsProblem))
        : rethrowAsProblem(httpError),
    ),
  );
};

function rethrowAsProblem(httpError: HttpErrorResponse) {
  const problem = normalizeToProblemDetails(httpError);

  // Log in development for debugging
  if (typeof ngDevMode !== 'undefined' && ngDevMode) {
    console.error(`[API Error] ${httpError.status} ${problem.title}`, problem.detail ?? '', problem.errors ?? '');
  }

  return throwError(() => problem);
}

/** The error with its Blob body parsed, when the body is JSON; otherwise the error as it is. */
async function withJsonBody(httpError: HttpErrorResponse): Promise<HttpErrorResponse> {
  const body = httpError.error as Blob;
  if (!body.type.includes('json')) return httpError;
  try {
    return new HttpErrorResponse({
      error: JSON.parse(await body.text()),
      headers: httpError.headers,
      status: httpError.status,
      statusText: httpError.statusText,
      url: httpError.url ?? undefined,
    });
  } catch {
    return httpError;
  }
}

/**
 * Converts an HttpErrorResponse into a ProblemDetails object.
 * If the response body is not a valid ProblemDetails, synthesizes one.
 */
function normalizeToProblemDetails(httpError: HttpErrorResponse): ProblemDetails {
  // If the server returned a valid ProblemDetails JSON body, use it
  if (isProblemDetails(httpError.error)) {
    return httpError.error;
  }

  // Network error (status === 0) or non-JSON response — synthesize ProblemDetails
  if (httpError.status === 0) {
    return {
      type: 'about:blank',
      title: 'NetworkError',
      detail: 'Network error. Check your connection and try again.',
      status: 0,
    };
  }

  // Non-ProblemDetails JSON error (e.g. string body) — wrap it
  const detail =
    typeof httpError.error === 'string'
      ? httpError.error
      : httpError.message || 'An unexpected error occurred.';

  return {
    type: 'about:blank',
    title: getTitleForStatus(httpError.status),
    detail,
    status: httpError.status,
  };
}

/** Maps HTTP status codes to machine-readable error titles. */
function getTitleForStatus(status: number): string {
  switch (status) {
    case 400:
      return 'Validation.General';
    case 401:
      return 'Auth.Unauthorized';
    case 403:
      return 'Auth.Forbidden';
    case 404:
      return 'Resource.NotFound';
    case 409:
      return 'Resource.Conflict';
    case 500:
      return 'Server.Failure';
    default:
      return 'Server.Unknown';
  }
}
